// @ts-nocheck
// #region ==================== IMPORTS ====================
import React, { useState, useMemo } from 'react';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Grid2,
  CircularProgress,
  Typography,
  Stack,
  IconButton,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import Menu, { MenuProps } from '@mui/material/Menu';
import { useDispatch, useSelector } from 'react-redux';
import AtomAccordion from '@/components/atoms/Accordion';
import AtomAutocomplete from '@/components/atoms/Autocomplete';
import AtomButton from '@/components/atoms/Button';
import AtomLineChart from '@/components/atoms/LineChart';
import AtomTabs from '@/components/atoms/Tabs';
import BriefingCard from '@/components/organisms/BriefingCard';
import OrgDialog from '@/components/organisms/Dialog';
import ModelStepper from '@/components/organisms/ModelStepper';
import StatusPieChart from '@/components/organisms/StatusPieChart';

import { useSafeEffect } from '@/hooks/useSafeEffect';
import styles from '@/pages/Dashboard/dashboard.module.css';
import { AppDispatch } from '@/store';
import { selectUser } from '@/store/auth';
import {
  selectBriefings,
  selectBriefingsLoading,
  selectBriefingsList,
} from '@/store/briefings/selectors';
import { briefings } from '@/store/briefings/thunks';
import { selectStudyData } from '@/store/data';
import { data } from '@/store/data/thunks';
import { setYearSelected } from '@/store/studies';
import {
  getAllStudies,
  getStudyId,
  getStudyName,
  getStudyProfile,
  getYearSelected,
  studiesLoading,
} from '@/store/studies/selectors';
import { studies } from '@/store/studies/thunks';
import { studyYears } from '@/store/study-year';
import { selectStudyChanges, selectCurrentYear } from '@/store/study-year/selectors';
import { downloadStudyYearExcel, getStudyYearById } from '@/store/study-year/thunks';
import { getUserStudies, userLoading } from '@/store/user/selectors';
import { user } from '@/store/user/thunks';
import { FileDownload } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
// #endregion

const Dashboard = () => {
  // #region ==================== DISPATCH ====================
  const dispatch = useDispatch<AppDispatch>();
  const [isLoading, setIsLoading] = useState(true);
  const [briefingFilter, setBriefingFilter] = useState('upcoming');
  // #endregion

  // #region ==================== STATES ====================
  const [addRelatedOpen, setAddRelatedOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [peers, setPeers] = useState<string[]>([]);
  const [selectedStudy, setSelectedStudy] = useState<{ label: string; value: Study } | null>(null);
  const [removePeerId, setRemovePeerId] = useState(null);
  const [chartData, setChartData] = useState({ mainStudyData: [], peersWithData: [] });
  const [isExporting, setIsExporting] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [isAddingPeer, setIsAddingPeer] = useState(false);
  const [isDeletingPeer, setIsDeletingPeer] = useState(new Set());

  // #endregion

  // #region ================== SELECTORS ================
  const useStudyId = useSelector(getStudyId);
  const useStudyName = useSelector(getStudyName);
  const useStudies = useSelector(getAllStudies);
  const useStudyProfile = useSelector(getStudyProfile);
  const useYearSelected = useSelector(getYearSelected);
  const useUser = useSelector(selectUser);
  const useBriefings = useSelector(selectBriefingsList);
  const useBriefingsLoading = useSelector(selectBriefingsLoading);
  const useUserStudies = useSelector(getUserStudies);
  const useStudiesLoading = useSelector(studiesLoading);
  const useUserLoading = useSelector(userLoading);
  const useStudyChanges = useSelector(selectStudyChanges);
  const useStudyData = useSelector(selectStudyData);
  const useCurrentYear = useSelector(selectCurrentYear);

  // #endregion

  // #region ================== COMPUTED VALUES ================
  // Find the current study's metrics based on selected year
  const currentStudyMetrics = useMemo(() => {
    if (!useUserStudies || !useYearSelected?.study_year_id) return [];

    // Find the study year that matches the selected year ID
    const selectedStudyYear = useUserStudies.find(
      studyYear => studyYear.study_year_id === useYearSelected.study_year_id
    );

    if (!selectedStudyYear?.metrics) return [];

    // Filter out metrics with count > 0 for the pie chart
    return selectedStudyYear.metrics.filter(metric => metric.count > 0);
  }, [useUserStudies, useYearSelected?.study_year_id]);

  // Check if we have valid metrics data
  const hasMetricsData = currentStudyMetrics && currentStudyMetrics.length > 0;

  // Check if we're loading metrics data
  const isLoadingMetrics = useUserLoading || useStudiesLoading || isLoading;
  // #endregion

  // #region ================== VARIABLE ================
  const gridItemStyle = {};

  const changesColumns: GridColDef[] = [
    {field: 'change_type',flex:1, headerName: 'Change Type', width: 150, valueFormatter: (params) => params?.replace('_', ' ')},
    {field: 'change_description',flex:2,  headerName: 'Description', width: 150},
    {field: 'last_edited_by',flex:1, headerName: 'User', width: 150, valueFormatter: (params) => `${params?.first_name} ${params?.last_name}`},
    {field: 'time_created', flex:1, headerName: 'Time Created', width: 150, valueFormatter: (params) => Date(params?.value).toLocaleString()},
  ];
  // #endregion

  // #region ================== FUNCTIONS ================
  const transformData = (input, key) => {
    if (!input || !Array.isArray(input)) {
      return [];
    }

    const transformed = input
      .filter(item => item && typeof item === 'object')
      .map(item => {
        const year = item.display_year || item.year || item.forecast_year;
        const value = item[key];

        if (year !== undefined && value !== undefined && value !== null) {
          return { x: year, y: Number(value) };
        }
        return null;
      })
      .filter(item => item !== null)
      .sort((a, b) => a.x - b.x); // Add sorting by year

    return transformed;
  };

  // Move BriefingsList inside Dashboard :TODO: Move to organisms
  const BriefingsList = ({ briefings, filter }) => {
    if (!briefings || briefings.length === 0) {
      return (
        <div className={styles.noBriefings}>
          <p className={styles.noBriefingsText}>No briefings available.</p>
        </div>
      );
    }
    console.log('BriefingsList', briefings, filter);
    const filteredBriefings = briefings.filter(briefing => {
      if (filter === 'upcoming') {
        return new Date(briefing.start_time) > new Date();
      } else {
        return new Date(briefing.start_time) < new Date();
      }
    });

    if (filteredBriefings.length === 0) {
      return (
        <div className={styles.noBriefings}>
          <p className={styles.noBriefingsText}>
            {`No ${filter} briefings found! Schedule a briefing in the Companies Table.`}
          </p>
        </div>
      );
    }

    return filteredBriefings.map(briefing => (
      <BriefingCard
        key={briefing.id}
        start_time={briefing.start_time}
        company_name={briefing.company_name || 'Unknown Company'}
        organizer={
          briefing.owner
            ? `${briefing.owner.first_name} ${briefing.owner.last_name}`
            : 'Unknown Organizer'
        }
        arc_attendees={briefing.user_attendees || []}
        contact_attendees={briefing.contact_attendees || []}
      />
    ));
  };

  const ChangesTable = ({ changes }) => {
    if (!changes || changes.length === 0) {
      return (
        <div className={styles.noChanges}>
          <p className={styles.noChangesText}>No changes available.</p>
        </div>
      );
    }
    return (
      <table className={styles.changesTable}>
        <thead>
          <tr>
            <th>Change Type</th>
            <th>Description</th>
            <th>User</th>
            <th>Time Created</th>
          </tr>
        </thead>
        <tbody>
          {changes.map(change => (
            <tr key={change.id}>
              <td>{change.change_type}</td>
              <td>{change.change_description || 'N/A'}</td>
              <td>{`${change.last_edited_by?.first_name} ${change.last_edited_by?.last_name}` || 'N/A'}</td>
              <td>{new Date(change.time_created).toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const handleBriefingFilter = (event, newFilter) => {
    if (newFilter !== null) {
      setBriefingFilter(newFilter);
    }
  };

  const makePeerSeries = peers => {
    if (!peers || !peers.length) return [];

    const colors = ['#01497c', '#FF69B4', '#D35400', '#ffcc00', '#2E8B57', '#D32F2F', '#4A90E2'];
    return peers.map((peer, index) => {
      return {
        id: peer.id,
        label: peer.name || peer.study_name,
        color: colors[index % colors.length],
        data: transformData(peer.history || [], 'revenue'),
      };
    });
  };

  // Generate data for charts
  const peerSeries = makePeerSeries(chartData.peersWithData || []);
  const mainStudyData = transformData(chartData.mainStudyData || [], 'revenue');

  const addPeer = async () => {
    if (!selectedStudy) return;

    try {
      setIsAddingPeer(true);
      // Get current peer studies from the study profile
      const currentPeers = useStudyProfile?.peer_studies || [];

      // Check if this peer is already in the list to avoid duplicates
      const peerExists = currentPeers.some(peer => peer.id === selectedStudy.value.id);

      if (!peerExists) {
        // Get just the IDs of current peers
        const peerIds = currentPeers.map(peer => peer.id);

        // Add the new peer ID to the existing list
        const newPeerIds = [...peerIds, selectedStudy.value.id];

        await handlePatchStudy(newPeerIds);
      }
      // Reset the OrgDialog
      setAddRelatedOpen(false);
      // Reset the selection
      setSelectedStudy(null);
    } catch (error) {
      console.log('Error adding peer:', error);
    } finally {
      setIsAddingPeer(false);
    }
  };

  // Function to remove a peer study
  const removePeer = async (peerId: string) => {
    // Add peerId to loading state
    setIsDeletingPeer(prev => new Set(prev).add(peerId));
    try {
      // Get current peer studies
      const currentPeers = useStudyProfile?.peer_studies || [];

      // Filter out the peer to be removed by ID and get only IDs
      const peerIds = currentPeers.filter(peer => peer.id !== peerId).map(peer => peer.id);

      await handlePatchStudy(peerIds);
    } catch (error) {
      console.error('Error removing peer:', error);
    } finally {
      setIsDeletingPeer(prev => {
        const newSet = new Set(prev);
        newSet.delete(peerId); // Remove peerId from loading state
        return newSet;
      });
    }
  };

  // Function to handle the study patch API call
  const handlePatchStudy = async (peerIds: string[]) => {
    try {
      // Make sure we use the correct study ID from the profile
      const correctStudyId = useStudyProfile?.id;

      if (!correctStudyId) {
        console.error('Cannot patch study: Study ID is missing from profile');
        return;
      }

      if (!useUser?.id) {
        console.error('Cannot patch study: User ID is missing');
        return;
      }

      console.log('Patching study with data:', {
        correctStudyId,
        userId: useUser.id,
        peerIds,
        studyProfile: useStudyProfile,
      });

      // Create patch data conforming to the API requirements
      const patchData = {
        user_id: useUser.id,
        study_id: correctStudyId,
        name: useStudyProfile?.name || useStudyName,
        description: useStudyProfile?.description || 'No description provided',
        technology_group: useStudyProfile?.technology_group || 'systems',
        automation_type: useStudyProfile?.automation_type || 'hybrid',
        peer_ids: peerIds, // Array of peer study IDs
      };

      console.log('Patch data being sent:', patchData);

      // Dispatch the patch study action
      const response = await dispatch(
        studies.updateStudy({
          studyId: correctStudyId,
          data: patchData,
        })
      );

      console.log('Patch study response:', response);

      // Refresh the study profile to get the updated peers
      await fetchStudyProfile();

      // Close add dialog
      setAddRelatedOpen(false);
    } catch (error) {
      console.error('Error in handlePatchStudy:', error);
    }
  };

  const handleYearChange = async event => {
    const newYearId = event.target.value;
    const selectedYearData = useStudyProfile?.years_available_data?.find(
      yearItem => yearItem.study_year_id === newYearId
    );
    if (selectedYearData) {
      //console.log("selecting year", selectedYearData);
      dispatch(setYearSelected(selectedYearData));
      await dispatch(getStudyYearById(selectedYearData.study_year_id));
    }
  };

  const handleExportToExcel = async () => {
    if (!useYearSelected?.study_year_id) {
      console.error('No study year selected for export');
      return;
    }

    setIsExporting(true);
    try {
      await dispatch(downloadStudyYearExcel(useYearSelected.study_year_id));
      // The download should be handled by the service/thunk
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      // You might want to show a toast/snackbar error message here
    } finally {
      setIsExporting(false);
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (action: string) => {
    handleMenuClose();

    switch (action) {
      case 'export':
        handleExportToExcel();
        break;
      case 'publish':
        // Handle publish study
        console.log('Publish Study clicked');
        break;
      case 'editContacts':
        // Handle edit contact list
        console.log('Edit Contact List clicked');
        break;
      case 'addCompany':
        // Handle add company
        console.log('Add Company clicked');
        break;
      case 'addSegment':
        // Handle add market segment
        console.log('Add Market Segment clicked');
        break;
      case 'scheduleBriefing':
        // Handle schedule briefing
        console.log('Schedule Briefing clicked');
        break;
      case 'editStudy':
        // Handle edit study details
        console.log('Edit Study Details clicked');
        break;
      default:
        break;
    }
  };

  // #endregion

  // #region ================== API CALLS ================
  const fetchStudyProfile = async () => {
    setIsLoading(true);
    try {
      const response = await dispatch(studies.getStudyProfile(useStudyId));

      // Initialize peers array from the response if available
      if (response.payload?.peer_studies) {
        const peerStudies = response.payload.peer_studies || [];
        const peerNames = peerStudies.map(study => study.study_name || study.name);
        setPeers(peerNames);

        // Fetch historical data for peer studies
        const peerDataPromises = peerStudies.map(async peer => {
          const peerData = await fetchStudyData(peer.id);
          return {
            ...peer,
            history: peerData,
          };
        });

        const peersWithData = await Promise.all(peerDataPromises);
        console.log('========:', useStudyProfile);

        // Also fetch main study data
        const mainStudyData = await fetchStudyData(useStudyId);

        // Update chart data
        setChartData({
          mainStudyData: mainStudyData,
          peersWithData: peersWithData,
        });
      }

      // // ADD THIS BLOCK: Call getStudyYearById with most recent year
      // if (
      //   response.payload?.years_available_data &&
      //   response.payload.years_available_data.length > 0
      // ) {
      //   // Find the most recent year (highest year value)
      //   const mostRecentYear = response.payload.years_available_data.reduce((latest, current) => {
      //     return current.year > latest.year ? current : latest;
      //   });

      //   // Call the thunk with the most recent study_year_id
      //   console.log('Fetching study year data for most recent year:', mostRecentYear);
      // }
    } catch (error) {
      console.error('Error fetching study profile:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStudyData = async (studyId = null) => {
    const targetStudyId = studyId || useStudyId;
    //console.log('Fetching study data for:', targetStudyId);

    try {
      const response = await dispatch(
        data.getStudyData({
          study_id__eq: targetStudyId,
          // order_by: 'year',
          include: ['revenue', 'units_sold', 'yoy_revenue', 'yoy_revenue_percentage'],
        })
      );

      console.log(`Study data response for ${targetStudyId}:`, response);
      return response.payload || [];
    } catch (error) {
      console.error(`Error fetching study data for ${targetStudyId}:`, error);
      return [];
    }
  };

  const fetchStudies = async () => {
    try {
      await dispatch(studies.getAllStudies());
    } catch (error) {
      console.error('Error fetching all studies:', error);
    }
  };

  // In the Dashboard component, update this part:
  const fetchUserStudies = async () => {
    try {
      if (useUser?.id) {
        const response = await dispatch(user.getUserStudies({ userId: useUser.id }));

        // Only set year if we don't have one (coming from home page)
        if (response.payload && !useYearSelected) {
          // Find studies for current study ID and get most recent year
          const currentStudyYears = response.payload.filter(
            studyYear => studyYear.study_id === useStudyId
          );

          if (currentStudyYears.length > 0) {
            const mostRecentStudyYear = currentStudyYears.sort((a, b) => b.year - a.year)[0];

            const yearData = {
              time_created: mostRecentStudyYear.time_created,
              time_updated: mostRecentStudyYear.time_updated,
              id: mostRecentStudyYear.id,
              study_id: mostRecentStudyYear.study_id,
              study_name: mostRecentStudyYear.study_name,
              study_year_id: mostRecentStudyYear.study_year_id,
              year: mostRecentStudyYear.year,
            };

            dispatch(setYearSelected(yearData));
            await dispatch(getStudyYearById(yearData.study_year_id));
          }
        }
      }
    } catch (error) {
      console.error('Error fetching user studies:', error);
    }
  };

  const fetchBriefings = async () => {
    try {
      // You can filter by study_id if needed
      await dispatch(
        briefings.getBriefings({
          study_id: useStudyId, // Optional: filter by current study
        })
      );
    } catch (error) {
      console.error('Error fetching briefings:', error);
    }
  };

  const fetchChanges = async () => {
    console.log('Fetching changes for year:', useYearSelected || useCurrentYear);
    if (useYearSelected || useCurrentYear) {
      const studyYearId = useYearSelected?.study_year_id || useCurrentYear?.study_year_id;
      try {
        // Fetch all changes related to the current study
        await dispatch(studyYears.getStudyYearChanges(studyYearId));
      } catch (error) {
        console.error('Error fetching study changes:', error);
      }
    }
  };
  // #endregion

  // #region ================== EFFECTS ================
  useSafeEffect(() => {
    Promise.all([
      fetchStudyProfile(),
      fetchStudies(),
      fetchUserStudies(),
      fetchChanges()
    ]);
  }, [dispatch, useStudyId, useUser?.id]);

  useSafeEffect(() => {
    if (useYearSelected?.study_year_id) {
      //console.log('useYearSelected', useYearSelected);
      fetchChanges();
      fetchStudyData();
    } else if (useCurrentYear) {
      fetchStudyData();
      fetchChanges()
    }
    

  }, [dispatch, useYearSelected, useCurrentYear]);

  useSafeEffect(() => {
    if (useCurrentYear) {
      console.log('Current Year Data from selectCurrentYear:', useCurrentYear);
    }
  }, [useCurrentYear]);

  useSafeEffect(() => {
    if (useStudyChanges) {
      console.log('Study Changes:', useStudyChanges);
    }
  }, [useStudyChanges]);
  // #endregion

  return (
    <div>
      <div className={styles.headContainer}>
        <h1 className={styles.header}>{useStudyName}</h1>
        <div className={styles.mainActions}>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel id="year-select-label">Year</InputLabel>
            <Select
              labelId="year-select-label"
              id="year-select"
              value={useYearSelected?.study_year_id || ''} // Extract study_year_id from the object
              label="Year"
              onChange={handleYearChange}
            >
              {useStudyProfile?.years_available_data
                ?.slice()
                ?.sort((a, b) => b.year - a.year)
                ?.map(yearItem => (
                  <MenuItem key={yearItem.study_year_id} value={yearItem.study_year_id}>
                    {yearItem.year}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          {/* Actions Menu */}
          <AtomButton
            className={styles.studyOptions}
            variant="contained"
            color="primary"
            onClick={handleMenuClick}
            aria-controls={open ? 'actions-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
          >
            Actions
          </AtomButton>

          <Menu
            id="actions-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            MenuListProps={{
              'aria-labelledby': 'actions-button',
            }}
          >
            <MenuItem
              onClick={() => handleMenuItemClick('export')}
              disabled={isExporting || !useYearSelected?.study_year_id}
            >
              {isExporting ? 'Exporting...' : 'Export to Excel'}
            </MenuItem>
            {/* <MenuItem onClick={() => handleMenuItemClick('publish')}>Publish Study</MenuItem> */}
            {/* <MenuItem onClick={() => handleMenuItemClick('editContacts')}>
              Edit Contact List
            </MenuItem>
            <MenuItem onClick={() => handleMenuItemClick('addCompany')}>Add a Company</MenuItem>
            <MenuItem onClick={() => handleMenuItemClick('addSegment')}>
              Add a Market Segment
            </MenuItem>
            <MenuItem onClick={() => handleMenuItemClick('scheduleBriefing')}>
              Schedule a Briefing
            </MenuItem>
            <MenuItem onClick={() => handleMenuItemClick('editStudy')}>Edit Study Details</MenuItem> */}
          </Menu>
        </div>
      </div>

      {/* Model Stepper - From Dashboard */}
      <div className={styles.modelCard}>
        <h1 className={styles.header}>The Study Process:</h1>
        <ModelStepper />
      </div>

      <Grid2
        container
        spacing={3}
        sx={{
          display: 'flex',
          flexDirection: 'wrap',
          flexWrap: 'wrap',
          width: '100%',
          gap: 2,
        }}
      >
        {/* Study History Chart Section - From StudyLanding */}
        <Grid2 size={8} sx={gridItemStyle} className={styles.modelCard}>
          <h1>Study History</h1>
          <div style={{ width: '100%' }}> {/* Removed height: '40vh' */}
            {console.log('Study History container rendered, no fixed height')} {/* Debug container */}
            <AtomLineChart
              showHorizontalGrid={true}
              showLegend={true}
              series={[
                {
                  id: 'main-study',
                  label: useStudyProfile?.name || useStudyName || 'Current Study',
                  color: '#2196f3',
                  data: mainStudyData,
                },
                ...peerSeries,
              ]}
              xAxis={{
                type: 'linear',
                min: 'dataMin',
                max: 'dataMax',
                domain: ['dataMin', 'dataMax'],
              }}
              height={400}
            />
          </div>
        </Grid2>

        {/* Status Pie Chart - Updated and Enabled */}
        <Grid2 size={4} sx={gridItemStyle} className={styles.modelCard}>
          <h1 className={styles.header}>Status Metrics</h1>
          {isLoadingMetrics ? (
            <CircularProgress sx={{ margin: '20px auto', display: 'block' }} />
          ) : hasMetricsData ? (
            <StatusPieChart metrics={currentStudyMetrics} />
          ) : (
            <Typography variant="body1" sx={{ padding: '16px', textAlign: 'center' }}>
              No metrics data available for this study year.
            </Typography>
          )}
        </Grid2>
      </Grid2>

      <Grid2
        container
        spacing={3}
        sx={{
          display: 'flex',
          flexDirection: 'wrap',
          flexWrap: 'wrap',
          width: '100%',
          gap: 2,
        }}
      >
        {/* Briefings Section - From Dashboard */}
        <Grid2 size={6} sx={gridItemStyle} className={styles.modelCard}>
          <h1 className={styles.header}>Briefings</h1>

          {useBriefingsLoading ? (
            <CircularProgress sx={{ margin: '20px auto', display: 'block' }} />
          ) : (
            <AtomTabs tabs={[{ label: 'Upcoming' }, { label: 'Completed' }]} variant="fullWidth">
              <div>
                <BriefingsList briefings={useBriefings || []} filter="upcoming" />
              </div>
              <div>
                <BriefingsList briefings={useBriefings || []} filter="completed" />
              </div>
            </AtomTabs>
          )}

          {/* <AtomButton
            variant="contained"
            color="primary"
            size="small"
            onClick={() => handleMenuItemClick('scheduleBriefing')}
          >
            Schedule a Briefing
          </AtomButton> */}
        </Grid2>

        {/* Related Studies Section - From StudyLanding */}
        <Grid2 size={6} sx={gridItemStyle} className={styles.modelCard}>
          <div className={styles.relatedStudies}>
            <h1 className={styles.header}>Related Studies</h1>
          </div>

          <AtomAccordion
            items={useStudyProfile?.peer_studies || []}
            summary={item => (
              <div className={styles.studyHeader}>
                <div>
                  <span className={styles.iconPlaceholder}></span>
                  {item.study_name || item.name}
                </div>
                <IconButton
                  size="small"
                  aria-label="delete"
                  color="primary"
                  onClick={e => {
                    e.stopPropagation();
                    removePeer(item.id);
                  }}
                  disabled={isDeletingPeer.has(item.id)}
                  aria-busy={isDeletingPeer.has(item.id) || undefined}
                >
                  {isDeletingPeer.has(item.id) ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <DeleteOutlined fontSize="small" />
                  )}
                </IconButton>
              </div>
            )}
            details={item => (
              <div className={styles.studyDescription}>
                <p>{item.description}</p>
              </div>
            )}
            customStyles={{
              summary: styles.customSummary,
              accordion: styles.customAccordion,
            }}
          />

          <AtomButton
            variant="contained"
            color="primary"
            size="small"
            onClick={() => setAddRelatedOpen(true)}
          >
            Add Related Study
          </AtomButton>
        </Grid2>
        <Grid2 size={12} sx={gridItemStyle} className={styles.modelCard}>
          <h1 className={styles.header}>Changes</h1>
          {useStudyChanges ? (
          <DataGrid rows={useStudyChanges} columns={changesColumns} autoHeight pageSize={5} rowsPerPageOptions={[5, 10, 20]} onRowClick={(row) => {console.log(row)} }/>
         
          ) : (
           <Typography variant="body1" sx={{ padding: '16px', textAlign: 'center' }}>
            No changes available for this study.
            </Typography>
          )}
        </Grid2>
      </Grid2>

      {/* Dialogs - From StudyLanding */}
      <OrgDialog open={addRelatedOpen} onClose={() => setAddRelatedOpen(false)}>
        <Typography variant="h4">Add a related study</Typography>
        <AtomAutocomplete
          label="Studies"
          placeholderText="Choose a study"
          value={selectedStudy}
          options={useStudies
            .filter(study => study.name !== useStudyName) // Filter out the current study
            .map(study => ({
              label: study.name,
              value: study,
              disabled: useStudyProfile?.peer_studies?.some(peer => peer.id === study.id) || false,
            }))}
          onChange={(_, newValue) => setSelectedStudy(newValue)}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={addPeer}
          disabled={!selectedStudy || isAddingPeer}
          sx={{ mt: 2 }}
        >
          {isAddingPeer ? <CircularProgress size={20} color='inherit' /> : 'Add Related Study'}
        </Button>
      </OrgDialog>

      <OrgDialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <h2>Are you sure you want to delete this related study?</h2>
        <Typography variant="body1">{selectedStudy?.name}</Typography>
        <Stack direction="row" spacing={2} marginTop={2}>
          <Button variant="contained" color="error" onClick={() => removePeer(removePeerId)}>
            Delete
          </Button>
          <Button variant="outlined" color="primary" onClick={() => setDeleteOpen(false)}>
            Cancel
          </Button>
        </Stack>
      </OrgDialog>
    </div>
  );
};

export default Dashboard;
